import Icon from "@/components/Icon/Icon";
import Input from "@/components/Input/Input";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { memo, useCallback } from "react";
import type { FieldRenderInputProps } from "../../types";
import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";

interface ChangeTextFieldProps extends BaseFieldProps {
  /** 最大文字数 */
  maxLength?: number;
  /** 最小文字数 */
  minLength?: number;
  /** プレースホルダー */
  placeholder?: string;
}

export const ChangeTextField = memo(
  ({
    fieldId,
    field,
    readOnly,
    disabled,
    className,
    maxLength,
    minLength,
    placeholder,
    "data-testid": testId,
  }: ChangeTextFieldProps) => {
    // 入力レンダリング関数
    const renderInput = useCallback(
      (props: FieldRenderInputProps) => {
        const tc = useCommonTranslation();
        // 現在の値を表示用に変換
        const displayPreviousValue =
          props.previousValue === null || props.previousValue === undefined
            ? tc("unregistered")
            : String(props.previousValue);

        // 入力値を文字列に変換
        const stringValue = props.value?.toString() ?? "";

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          props.onChange(newValue === "" ? undefined : newValue);
        };

        return (
          <HStack className="items-center justify-center w-full gap-4">
            {/* 現在の値（読み取り専用のテキスト表示） */}
            <div className="flex-1 justify-start min-w-0">
              <Text
                variant="labelSemiLarge"
                className="text-neutral-on-fill break-words"
              >
                {displayPreviousValue}
              </Text>
            </div>

            {/* Arrow Icon */}
            <div className="items-center flex-shrink-0 self-center pr-3 pl-3">
              <Icon
                iconName="ArrowBigRightIcon"
                className="w-4 h-4 text-primary-on-neutral-fill"
              />
            </div>

            {/* 新しい値の入力 */}
            <div className="flex-1 min-w-0">
              {props.readOnly ? (
                <Text
                  variant="labelSemiLarge"
                  className="text-neutral-on-fill break-words"
                >
                  {stringValue || tc("unregistered")}
                </Text>
              ) : (
                // 通常時は入力フィールドを表示
                <Input
                  id={props.id}
                  name={props.name}
                  type="text"
                  value={stringValue}
                  onChange={handleChange}
                  onFocus={props.onFocus}
                  onBlur={props.onBlur}
                  placeholder={props.placeholder ?? placeholder}
                  disabled={props.disabled}
                  readOnly={props.readOnly}
                  required={props.required}
                  maxLength={maxLength}
                  minLength={minLength}
                  error={!!props.error}
                  aria-invalid={!!props.error}
                  aria-describedby={
                    props.error ? `${props.id}-error` : undefined
                  }
                  width="100%"
                  className="w-full"
                  data-testid={props["data-testid"] || "input-field"}
                />
              )}
            </div>
          </HStack>
        );
      },
      [placeholder, maxLength, minLength],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        readOnly={readOnly}
        disabled={disabled}
        layout="vertical" // UI 的にラベルと入力フィールドが縦並びじゃないとしんどいので
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);
